/**
 * BondTypeConfigsView — Brief 4 V2 · Tela 9 Configuração de Bonds (ADR-037/131).
 *
 * O Gestor vê os 21 tipos canônicos agrupados em 5 famílias (rótulo + defaults +
 * contagem de Bonds), busca por tipo e liga/desliga os que a operação usa
 * (toggle · PATCH If-Match). NetComet inicia com `contract` + `customer-account`.
 *
 * Honest-defer (sem fonte no backend nesta fatia): a config rica por tipo
 * (windowConfig, override de adversarial/side, hierarquia/cross-reference,
 * migração de Bonds existentes via worker) e o badge "Regulated". O painel
 * "editar" mostra os defaults canônicos em LEITURA — não inventa inputs que não
 * persistem. Toggle gateado em `bond-type-config-manage`; sem ele, leitura.
 */

import { useMemo, useState } from 'react';

import { ApiError } from '../../lib/api-client';
import { useToastStore } from '../../stores/toast-store';

import { adversarialModeLabel, bondLifecycleLabel, bondSideLabel } from './bond-type-labels';
import { BOND_FAMILIES } from './types';
import { useToggleBondTypeConfig } from './use-bond-type-configs';

import type { BondTypeConfigView } from './types';

export interface BondTypeConfigsViewProps {
  configs: BondTypeConfigView[];
  canManage: boolean;
}

function matchesQuery(config: BondTypeConfigView, query: string): boolean {
  if (query.length === 0) return true;
  const q = query.toLowerCase();
  return config.label.toLowerCase().includes(q) || config.bondType.toLowerCase().includes(q);
}

export function BondTypeConfigsView({ configs, canManage }: BondTypeConfigsViewProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const toggle = useToggleBondTypeConfig();
  const toast = useToastStore((s) => s.push);

  const byType = useMemo(() => {
    const map = new Map<string, BondTypeConfigView>();
    for (const c of configs) map.set(c.bondType, c);
    return map;
  }, [configs]);

  const enabledCount = configs.filter((c) => c.enabled).length;
  const totalCount = configs.length;

  // Tipos que o catálogo canônico do FE ainda não mapeou para nenhuma família —
  // mostrados ao final para não esconder dado real caso o catálogo cresça.
  const mappedTypes = new Set(BOND_FAMILIES.flatMap((f) => f.types));
  const unmappedConfigs = configs.filter((c) => !mappedTypes.has(c.bondType));

  const editingConfig = editing !== null ? byType.get(editing) : undefined;

  const handleToggle = (config: BondTypeConfigView): void => {
    if (!canManage || toggle.isPending) return;
    toggle.mutate(
      { bondType: config.bondType, expectedVersion: config.version, enabled: !config.enabled },
      {
        onSuccess: (updated) => {
          toast({
            variant: 'success',
            title: updated.enabled
              ? `Tipo "${updated.label}" ativado`
              : `Tipo "${updated.label}" desativado`,
            durationMs: 3000,
          });
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            // Versão divergente — a lista é reinvalidada automaticamente; nada se perde.
            toast({
              variant: 'info',
              title: 'A lista foi atualizada por outra alteração',
              description: 'Recarregamos os tipos. Tente o toggle novamente.',
              durationMs: 5000,
            });
            return;
          }
          toast({
            variant: 'error',
            title: 'Não foi possível alterar o tipo de Bond.',
            durationMs: 6000,
          });
        },
      },
    );
  };

  return (
    <div className="bond-type-configs-screen">
      <header className="btc-pagehead">
        <div>
          <div className="btc-pagehead__crumb">Configuração</div>
          <h1 className="btc-pagehead__title">Configuração de Bonds</h1>
          <p className="btc-pagehead__sub">
            21 tipos canônicos · 5 famílias ·{' '}
            <strong>
              {enabledCount}/{totalCount}
            </strong>{' '}
            ativos no tenant
          </p>
        </div>
        <div className="btc-pagehead__spacer" />
        <input
          type="search"
          className="btc-search"
          placeholder="Buscar tipo…"
          aria-label="Buscar tipo de Bond"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </header>

      <div className="btc-page">
        <div className="btc-banner btc-banner--info" role="note">
          <span>
            21 tipos de vínculo disponíveis no catálogo da plataforma. Ative os tipos que sua
            operação usa — os demais ficam ocultos para os atendentes.
          </span>
        </div>

        {!canManage && (
          <div className="btc-banner btc-banner--muted" role="note">
            <span>
              Você tem acesso de <strong>leitura</strong>. Ativar ou desativar tipos de vínculo é
              restrito ao administrador do tenant.
            </span>
          </div>
        )}

        <div className="btc-families">
          {BOND_FAMILIES.map((family) => {
            const rows = family.types
              .map((typeId) => byType.get(typeId))
              .filter((c): c is BondTypeConfigView => c !== undefined && matchesQuery(c, search));
            if (rows.length === 0) return null;
            return (
              <section
                key={family.id}
                className="btc-family"
                aria-label={`Família ${family.label}`}
              >
                <h2 className="btc-family__title">{family.label}</h2>
                {rows.map((config) => (
                  <BondCatalogRow
                    key={config.bondType}
                    config={config}
                    canManage={canManage}
                    isToggling={toggle.isPending}
                    onToggle={handleToggle}
                    onEdit={(c) => setEditing(c.bondType)}
                  />
                ))}
              </section>
            );
          })}

          {unmappedConfigs.length > 0 && (
            <section className="btc-family" aria-label="Família Não classificados">
              <h2 className="btc-family__title">Não classificados</h2>
              {unmappedConfigs
                .filter((c) => matchesQuery(c, search))
                .map((config) => (
                  <BondCatalogRow
                    key={config.bondType}
                    config={config}
                    canManage={canManage}
                    isToggling={toggle.isPending}
                    onToggle={handleToggle}
                    onEdit={(c) => setEditing(c.bondType)}
                  />
                ))}
            </section>
          )}
        </div>

        {editingConfig !== undefined && (
          <BondTypeDetailPanel config={editingConfig} onClose={() => setEditing(null)} />
        )}
      </div>
    </div>
  );
}

interface BondCatalogRowProps {
  config: BondTypeConfigView;
  canManage: boolean;
  isToggling: boolean;
  onToggle: (config: BondTypeConfigView) => void;
  onEdit: (config: BondTypeConfigView) => void;
}

function BondCatalogRow({
  config,
  canManage,
  isToggling,
  onToggle,
  onEdit,
}: BondCatalogRowProps): JSX.Element {
  return (
    <div className={`btc-row${config.enabled ? ' btc-row--enabled' : ''}`}>
      <button
        type="button"
        role="switch"
        aria-checked={config.enabled}
        aria-label={`${config.enabled ? 'Desativar' : 'Ativar'} tipo ${config.label}`}
        className="btc-switch"
        disabled={!canManage || isToggling}
        onClick={() => onToggle(config)}
      >
        <span className="btc-switch__track" />
      </button>
      <div className="btc-row__main">
        {/* id canônico fica no title (diagnóstico) — não é copy do operador */}
        <span className="btc-row__name" title={config.bondType}>
          {config.label}
        </span>
      </div>
      <span className="btc-row__count">
        {config.activeBondsCount > 0
          ? `${config.activeBondsCount.toString()} ${config.activeBondsCount === 1 ? 'Bond' : 'Bonds'}`
          : ''}
      </span>
      <button type="button" className="btc-link" onClick={() => onEdit(config)}>
        ver defaults
      </button>
    </div>
  );
}

interface BondTypeDetailPanelProps {
  config: BondTypeConfigView;
  onClose: () => void;
}

function BondTypeDetailPanel({ config, onClose }: BondTypeDetailPanelProps): JSX.Element {
  return (
    <aside className="btc-panel" aria-label={`Defaults de ${config.label}`}>
      <div className="btc-panel__head">
        <h2 className="btc-panel__title" title={config.bondType}>
          {config.label}
        </h2>
        <button type="button" className="btc-link" onClick={onClose}>
          Fechar
        </button>
      </div>

      <p className="btc-panel__hint">
        {config.activeBondsCount > 0
          ? `${config.activeBondsCount.toString()} Bonds deste tipo no tenant.`
          : 'Nenhum Bond deste tipo no tenant.'}{' '}
        Defaults canônicos do catálogo (leitura).
      </p>

      <dl className="btc-defaults">
        <div className="btc-defaults__row">
          <dt>Perfil adversarial</dt>
          <dd title={config.defaultAdversarialMode}>
            {adversarialModeLabel(config.defaultAdversarialMode)}
          </dd>
        </div>
        <div className="btc-defaults__row">
          <dt>Lado padrão</dt>
          <dd title={config.defaultSide ?? undefined}>
            {config.defaultSide !== null ? (
              bondSideLabel(config.defaultSide)
            ) : (
              <span className="btc-muted">—</span>
            )}
          </dd>
        </div>
        <div className="btc-defaults__row">
          <dt>Ciclo de vida</dt>
          <dd title={config.defaultLifecycle}>{bondLifecycleLabel(config.defaultLifecycle)}</dd>
        </div>
        <div className="btc-defaults__row">
          <dt>Status no tenant</dt>
          <dd>{config.enabled ? 'Ativo' : 'Inativo'}</dd>
        </div>
      </dl>

      <div className="btc-banner btc-banner--muted" role="note">
        <span>
          A configuração avançada por tipo (janelas de Sequence, override de adversarial/side,
          hierarquia e cross-reference, migração de Bonds existentes) ainda não é exposta pelo
          backend — chega numa fatia futura com fonte real. Por ora os defaults acima são canônicos.
        </span>
      </div>
    </aside>
  );
}
